import { Chip } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { RowCellContent, Table, TableBody } from "@dash/table";
import { Tile } from "@dash/tile";
import { useTheme } from "@emotion/react";
import { Link } from "react-router-dom-v5-compat";

import { CartWithDetails } from "@megaron/fulfillment-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { Uuid } from "@megaron/uuid";

import { CartStatusChips } from "./CartStatusChips";

type Props = {
  carts: CartWithDetails[];
  isLoading: boolean;
};

export const CartsTable: React.FC<Props> = ({ carts, isLoading }) => {
  const theme = useTheme();
  const { isMobile } = useDeviceType();

  const tableColumns = [
    {},
    ...tableHeadingList.map(
      ({ heading }) =>
        ({
          isSortable: false,
          label: heading,
        }) as const,
    ),
  ];

  const getTableRowCellsContent = (cart: CartWithDetails): RowCellContent[] => {
    const rewardsCount = cart.rewards.length;
    const itemsCount = cart.items.length;

    return [
      {
        isLink: true,
        element: (
          <div
            css={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        ),
      },
      {
        isLink: true,
        element: <div css={{ display: "flex", alignItems: "center", gap: 4 }}>{cart.id}</div>,
      },
      {
        isLink: true,
        element: (
          <>
            {cart.createdAt.toLocaleString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </>
        ),
      },
      {
        isLink: true,
        element: (
          <>
            {cart.updatedAt.toLocaleString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </>
        ),
      },
      {
        isLink: true,
        element: cart.userId ? (
          <Link
            to={getMegaraxPath.uri("megarax://crm/customer/{id}", { id: Uuid(cart.userId) })}
            onClick={(e) => e.stopPropagation()}
            css={{
              color: "inherit",
              textDecoration: "underline",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {cart.userId}
          </Link>
        ) : (
          <span>Anonimowy</span>
        ),
      },
      {
        isLink: true,
        element: (
          <Chip color={rewardsCount > 0 ? theme.colors.primary : theme.colors.default} variant="secondary">
            {rewardsCount}
          </Chip>
        ),
      },
      {
        isLink: true,
        element: (
          <Chip color={itemsCount > 0 ? theme.colors.primary : theme.colors.default} variant="secondary">
            {itemsCount}
          </Chip>
        ),
      },
      {
        isLink: true,
        element: <CartStatusChips isArchived={cart.isArchived} isFulfilled={cart.isFulfilled} />,
      },
    ];
  };

  if (isMobile) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "0.625rem", width: "100%", marginBottom: "1rem" }}>
        {carts.map((cart) => {
          const rewardsCount = cart.rewards.length;
          const itemsCount = cart.items.length;

          return (
            <Link
              key={cart.id}
              to={`/specjalista/carts/id/${cart.id}`}
              css={{ textDecoration: "none", color: "inherit" }}
            >
              <Tile>
                <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", overflow: "hidden" }}>
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span css={{ fontWeight: 600, color: theme.colors.primary }}>ID:</span>
                    <span css={{ color: theme.colors.primary }}>{cart.id}</span>
                  </div>
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <span css={{ fontWeight: 600 }}>Status:</span>
                    <CartStatusChips isArchived={cart.isArchived} isFulfilled={cart.isFulfilled} />
                  </div>
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span css={{ fontWeight: 600 }}>Użytkownik:</span>
                    {cart.userId ? (
                      <Link
                        to={getMegaraxPath.uri("megarax://crm/customer/{id}", { id: Uuid(cart.userId) })}
                        onClick={(e) => e.stopPropagation()}
                        css={{
                          color: theme.colors.primary,
                          textDecoration: "underline",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {cart.userId}
                      </Link>
                    ) : (
                      <span>Anonimowy</span>
                    )}
                  </div>
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <span css={{ fontWeight: 600 }}>Nagrody:</span>
                    <Chip color={rewardsCount > 0 ? theme.colors.primary : theme.colors.default} variant="secondary">
                      {rewardsCount}
                    </Chip>
                    <span css={{ fontWeight: 600, marginLeft: "0.5rem" }}>Produkty:</span>
                    <Chip color={itemsCount > 0 ? theme.colors.primary : theme.colors.default} variant="secondary">
                      {itemsCount}
                    </Chip>
                  </div>
                  <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                    <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span css={{ fontWeight: 600 }}>Utworzono:</span>
                      <span css={{ fontSize: "0.875rem" }}>
                        {cart.createdAt.toLocaleString("pl-PL", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span css={{ fontWeight: 600 }}>Zaktualizowano:</span>
                      <span css={{ fontSize: "0.875rem" }}>
                        {cart.updatedAt.toLocaleString("pl-PL", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </Tile>
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table columns={tableColumns} css={{ padding: "0 2rem" }}>
        <TableBody
          isLoading={isLoading}
          rows={carts.map((cart) => ({
            uuid: cart.id,
            linkTo: `/specjalista/carts/id/${cart.id}`,
            cellsContent: getTableRowCellsContent(cart),
            css: {
              "&:hover": {
                "td:nth-child(1)": {
                  textDecoration: "underline",
                },
              },
            },
          }))}
        />
      </Table>
    </div>
  );
};

const tableHeadingList: { heading: string }[] = [
  { heading: "ID Koszyka" },
  { heading: "Data utworzenia" },
  { heading: "Ostatnia aktualizacja" },
  { heading: "Użytkownik" },
  { heading: "Liczba nagród" },
  { heading: "Liczba produktów" },
  { heading: "Status" },
];
